import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api-client';
import { cn } from '../lib/cn';

type TenantSummary = {
  tenantId: string;
  accountNumber: string;
  businessName: string;
  industry: string | null;
  planType: string;
  subscriptionStatus: string;
  businessPhone: string | null;
  twilioPhoneNumber: string | null;
  createdAt: string | null;
};

type TenantListResponse = {
  tenants: TenantSummary[];
  nextCursor: string | null;
  count: number;
  scannedCount: number;
};

/**
 * GET /tenants — powered by `lambda/admin/tenants-list/`.
 * See docs/PROCESS-admin-customer-record.md §11.1 for the full contract.
 */
export function TenantListPage() {
  const [q, setQ] = useState('');
  const [plan, setPlan] = useState('');
  const [status, setStatus] = useState('');

  const query = useQuery({
    queryKey: ['tenants', { q, plan, status }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (plan) params.set('plan', plan);
      if (status) params.set('status', status);
      const qs = params.toString();
      return api.get<TenantListResponse>(`/tenants${qs ? `?${qs}` : ''}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-lg font-semibold">Tenants</h1>
          <p className="text-sm text-muted">
            {query.data
              ? `${query.data.count} showing · ${query.data.scannedCount} total`
              : 'Loading…'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="panel p-3 flex gap-2 items-center text-sm">
        <input
          type="search"
          placeholder="Search name or account #"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 px-2 py-1 bg-bg border border-border rounded focus:border-accent outline-none"
        />
        <select
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="px-2 py-1 bg-bg border border-border rounded outline-none"
        >
          <option value="">All plans</option>
          <option value="starter">Starter</option>
          <option value="growth">Growth</option>
          <option value="professional">Professional</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="px-2 py-1 bg-bg border border-border rounded outline-none"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="past_due">Past due</option>
          <option value="cancelled">Cancelled</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>

      {/* Results */}
      {query.isLoading && <LoadingBlock />}
      {query.isError && <ErrorBlock error={query.error} />}
      {query.data && query.data.tenants.length === 0 && <EmptyBlock />}
      {query.data && query.data.tenants.length > 0 && <ResultsTable tenants={query.data.tenants} />}
    </div>
  );
}

function ResultsTable({ tenants }: { tenants: TenantSummary[] }) {
  return (
    <div className="panel overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-bg text-xs text-muted uppercase tracking-wide">
          <tr>
            <Th>Business</Th>
            <Th>Account #</Th>
            <Th>Industry</Th>
            <Th>Plan</Th>
            <Th>Status</Th>
            <Th>Phone</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tenants.map((t) => (
            <tr key={t.tenantId} className="hover:bg-bg/60">
              <Td>
                <Link to={`/tenants/${t.tenantId}`} className="text-fg hover:text-accent font-medium">
                  {t.businessName}
                </Link>
                <div className="text-xs text-muted mt-0.5">
                  <code>{t.tenantId}</code>
                </div>
              </Td>
              <Td className="font-mono text-xs">{t.accountNumber}</Td>
              <Td className="text-xs">{t.industry ?? <span className="text-muted">—</span>}</Td>
              <Td>
                <Badge value={t.planType} />
              </Td>
              <Td>
                <StatusBadge status={t.subscriptionStatus} />
              </Td>
              <Td className="text-xs font-mono">
                {t.twilioPhoneNumber ?? t.businessPhone ?? <span className="text-muted">—</span>}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('px-3 py-2 align-top', className)}>{children}</td>;
}

function Badge({ value }: { value: string }) {
  return (
    <span className="inline-block text-xs px-1.5 py-0.5 rounded bg-border/60 text-fg">
      {value}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'active'
      ? 'bg-accent/10 text-accent'
      : status === 'past_due'
      ? 'bg-danger/10 text-danger'
      : status === 'cancelled' || status === 'suspended'
      ? 'bg-muted/10 text-muted'
      : 'bg-border/60 text-fg';
  return <span className={cn('inline-block text-xs px-1.5 py-0.5 rounded', tone)}>{status}</span>;
}

function LoadingBlock() {
  return <div className="panel p-8 text-center text-sm text-muted">Loading tenants…</div>;
}

function EmptyBlock() {
  return (
    <div className="panel p-8 text-center text-sm">
      <div className="text-fg font-medium">No tenants match these filters</div>
      <div className="text-xs text-muted mt-1">Try clearing the search or filter selectors.</div>
    </div>
  );
}

function ErrorBlock({ error }: { error: unknown }) {
  const msg =
    error instanceof ApiError
      ? `${error.status} · ${error.code} · ${error.message}`
      : error instanceof Error
      ? error.message
      : 'unknown error';
  return (
    <div className="panel p-4 text-sm text-danger bg-danger/5 border-danger/30">
      <div className="font-medium">Failed to load tenants</div>
      <div className="text-xs mt-1 font-mono">{msg}</div>
    </div>
  );
}
